package er.prateek.crossly.ui.achievements

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.spring
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeContentPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.rotate
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import crossly.shared.generated.resources.Res
import crossly.shared.generated.resources.achievement_awesome
import crossly.shared.generated.resources.achievement_share
import crossly.shared.generated.resources.achievement_unlocked_kicker
import crossly.shared.generated.resources.share_badge_text
import er.prateek.crossly.di.AppContainer
import er.prateek.crossly.firebase.AnalyticsEvents
import er.prateek.crossly.progression.achievements.Achievement
import er.prateek.crossly.progression.achievements.AchievementRarity
import er.prateek.crossly.ui.components.ConfettiBurst
import er.prateek.crossly.ui.components.GameButton
import er.prateek.crossly.ui.components.GameButtonSize
import er.prateek.crossly.ui.components.GameButtonStyle
import er.prateek.crossly.ui.components.SparkBurst
import er.prateek.crossly.ui.theme.Radius
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.stringResource

/**
 * Lets a screen's own modal moment (e.g. the "Streak restored!" sheet) finish before a badge
 * celebration plays -- two celebrations at once cancel each other out. Hold it with
 * [HoldCelebrations] for as long as the moment is on screen.
 */
object CelebrationGate {
    var holds by mutableIntStateOf(0)
        private set

    internal fun acquire() { holds++ }
    internal fun release() { holds = (holds - 1).coerceAtLeast(0) }
}

@Composable
fun HoldCelebrations() {
    DisposableEffect(Unit) {
        CelebrationGate.acquire()
        onDispose { CelebrationGate.release() }
    }
}

/** How long a freshly arrived unlock waits, so a results screen's own reward beat lands first. */
private const val PRESENT_DELAY_MS = 1_500L

/**
 * App-level host for unlock celebrations. Sits above the nav host and plays queued unlocks one
 * at a time, but only while [canPresent] -- never over a live board, where it would steal a
 * timed run's focus. Unlocks earned mid-Brainstorm wait for the between-games break screen.
 */
@Composable
fun AchievementCelebrationHost(canPresent: Boolean) {
    @Suppress("NAME_SHADOWING")
    val canPresent = canPresent && CelebrationGate.holds == 0
    val manager = AppContainer.achievementManager
    val next = manager.pending.firstOrNull()
    var showing by remember { mutableStateOf<Achievement?>(null) }

    LaunchedEffect(next, canPresent) {
        if (next == null || !canPresent) {
            if (!canPresent) showing = null
            return@LaunchedEffect
        }
        // Back-to-back unlocks follow quickly; the first one waits for the screen to settle.
        delay(if (showing == null) PRESENT_DELAY_MS else 250L)
        showing = next
    }

    AnimatedVisibility(
        visible = showing != null && canPresent,
        enter = fadeIn(tween(220)),
        exit = fadeOut(tween(180)),
    ) {
        val achievement = showing ?: return@AnimatedVisibility
        AchievementUnlockedOverlay(
            achievement = achievement,
            onDismiss = {
                manager.consumeCelebration(achievement)
                showing = null
            },
        )
    }
}

@Composable
fun AchievementUnlockedOverlay(
    achievement: Achievement,
    onDismiss: () -> Unit,
) {
    val colors = MaterialTheme.game
    val tint = achievement.rarity.color()
    val haptics = LocalHapticFeedback.current
    val title = stringResource(achievement.titleRes)
    val description = stringResource(achievement.descriptionRes)
    val shareText = stringResource(Res.string.share_badge_text, title, description)
    val big = achievement.rarity >= AchievementRarity.EPIC

    val medalScale = remember(achievement) { Animatable(0.2f) }
    val medalSpin = remember(achievement) { Animatable(-35f) }
    val textAlpha = remember(achievement) { Animatable(0f) }
    LaunchedEffect(achievement) {
        haptics.performHapticFeedback(HapticFeedbackType.LongPress)
        AppContainer.analytics.logEvent(
            AnalyticsEvents.ACHIEVEMENT_CELEBRATED,
            mapOf("achievement_id" to achievement.id, "rarity" to achievement.rarity.name),
        )
        medalSpin.animateTo(0f, spring(dampingRatio = 0.45f, stiffness = Spring.StiffnessLow))
    }
    LaunchedEffect(achievement) {
        medalScale.animateTo(1f, spring(dampingRatio = 0.42f, stiffness = 260f))
    }
    LaunchedEffect(achievement) {
        delay(260)
        textAlpha.animateTo(1f, tween(420))
    }

    val ambient = rememberInfiniteTransition(label = "badgeAmbient")
    val rayTurn by ambient.animateFloat(
        initialValue = 0f,
        targetValue = 360f,
        animationSpec = infiniteRepeatable(tween(16_000, easing = LinearEasing)),
        label = "rays",
    )
    val bob by ambient.animateFloat(
        initialValue = -4f,
        targetValue = 4f,
        animationSpec = infiniteRepeatable(tween(1_600), RepeatMode.Reverse),
        label = "bob",
    )
    val glow by ambient.animateFloat(
        initialValue = 0.55f,
        targetValue = 0.9f,
        animationSpec = infiniteRepeatable(tween(1_200), RepeatMode.Reverse),
        label = "glow",
    )

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color.Black.copy(alpha = 0.78f))
            // Swallow taps so nothing underneath reacts while the moment plays.
            .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) {},
        contentAlignment = Alignment.Center,
    ) {
        if (big) ConfettiBurst(seed = achievement.ordinal + 7, pieceCount = 90)

        Column(
            modifier = Modifier
                .safeContentPadding()
                .padding(horizontal = Spacing.xl)
                .widthIn(max = 420.dp)
                .fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Box(modifier = Modifier.size(300.dp), contentAlignment = Alignment.Center) {
                // Sunburst + halo, then sparks, then the medal on top.
                Canvas(modifier = Modifier.fillMaxSize()) {
                    val r = size.minDimension / 2f
                    drawCircle(
                        brush = Brush.radialGradient(
                            listOf(tint.copy(alpha = 0.55f * glow), tint.copy(alpha = 0f)),
                            center = center,
                            radius = r,
                        ),
                        radius = r,
                    )
                    rotate(rayTurn) {
                        val rays = 14
                        val half = (PI / rays / 2).toFloat()
                        repeat(rays) { i ->
                            val a = (2 * PI * i / rays).toFloat()
                            val path = Path().apply {
                                moveTo(center.x, center.y)
                                lineTo(center.x + cos(a - half) * r, center.y + sin(a - half) * r)
                                lineTo(center.x + cos(a + half) * r, center.y + sin(a + half) * r)
                                close()
                            }
                            drawPath(
                                path,
                                brush = Brush.radialGradient(
                                    listOf(tint.copy(alpha = 0.32f), Color.Transparent),
                                    center = center,
                                    radius = r,
                                ),
                            )
                        }
                    }
                    drawCircle(Color.White.copy(alpha = 0.10f * glow), radius = r * 0.42f, center = Offset(center.x, center.y))
                }
                // SparkBurst fires from 38% down its frame; nudge it so the burst leaves the medal's centre.
                SparkBurst(
                    seed = achievement.ordinal + 1,
                    pieceCount = if (big) 110 else 70,
                    modifier = Modifier.graphicsLayer { translationY = size.height * 0.12f },
                )
                AchievementMedal(
                    achievement = achievement,
                    unlocked = true,
                    size = 148.dp,
                    modifier = Modifier
                        .graphicsLayer {
                            scaleX = medalScale.value
                            scaleY = medalScale.value
                            rotationZ = medalSpin.value
                            translationY = bob.dp.toPx()
                        },
                )
            }

            Column(
                modifier = Modifier.graphicsLayer { alpha = textAlpha.value; translationY = (1f - textAlpha.value) * 24.dp.toPx() },
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(Spacing.sm),
            ) {
                Text(
                    text = stringResource(Res.string.achievement_unlocked_kicker),
                    style = MaterialTheme.typography.labelLarge.copy(letterSpacing = 3.sp, fontWeight = FontWeight.Black),
                    color = tint,
                )
                Text(
                    text = title,
                    style = MaterialTheme.typography.displaySmall,
                    color = Color.White,
                    textAlign = TextAlign.Center,
                )
                RarityPill(achievement.rarity)
                Text(
                    text = description,
                    style = MaterialTheme.typography.bodyLarge,
                    color = Color.White.copy(alpha = 0.78f),
                    textAlign = TextAlign.Center,
                    modifier = Modifier.padding(top = Spacing.xs),
                )
                Column(
                    modifier = Modifier.fillMaxWidth().padding(top = Spacing.xl),
                    verticalArrangement = Arrangement.spacedBy(Spacing.sm),
                ) {
                    GameButton(
                        text = stringResource(Res.string.achievement_awesome),
                        onClick = onDismiss,
                        style = GameButtonStyle.GOLD,
                        size = GameButtonSize.LARGE,
                        fillWidth = true,
                    )
                    GameButton(
                        text = stringResource(Res.string.achievement_share),
                        onClick = {
                            AppContainer.analytics.logEvent(
                                AnalyticsEvents.ACHIEVEMENT_SHARED,
                                mapOf("achievement_id" to achievement.id),
                            )
                            AppContainer.shareManager.shareWithAppLink(
                                AppContainer.shareManager.playerName(),
                                shareText,
                            )
                        },
                        // The scrim is always dark, so the ghost style's muted ink would vanish here.
                        style = GameButtonStyle.SECONDARY,
                        fillWidth = true,
                    )
                }
            }
        }
    }
}

@Composable
fun RarityPill(rarity: AchievementRarity, modifier: Modifier = Modifier) {
    val tint = rarity.color()
    val shape = RoundedCornerShape(Radius.chip)
    Text(
        text = stringResource(rarity.labelRes).uppercase(),
        style = MaterialTheme.typography.labelSmall.copy(letterSpacing = 1.5.sp, fontWeight = FontWeight.Bold),
        color = tint,
        modifier = modifier
            .background(tint.copy(alpha = 0.16f), shape)
            .border(1.dp, tint.copy(alpha = 0.5f), shape)
            .padding(horizontal = Spacing.md, vertical = Spacing.xs),
    )
}
